import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef } from "react";
import { Star } from "lucide-react";
import { MEDIA } from "../../data/media";
import { scrollToId, ease } from "../../lib/motion";

const HEADLINE = "You don't have to face everything alone.";

const Headline = () => {
  const reduced = useReducedMotion();
  return (
    <motion.span
      className="block"
      initial={{ y: reduced ? 0 : 28, opacity: reduced ? 1 : 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.1, ease, delay: reduced ? 0 : 0.4 }}
    >
      {HEADLINE}
    </motion.span>
  );
};

export const Hero = () => {
  const imgRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y > window.innerHeight) return;
        if (imgRef.current) imgRef.current.style.transform = `translate3d(0, ${y * 0.18}px, 0) scale(1.08)`;
        if (contentRef.current) {
          contentRef.current.style.transform = `translate3d(0, ${y * 0.28}px, 0)`;
          contentRef.current.style.opacity = `${Math.max(0, 1 - y / (window.innerHeight * 0.8))}`;
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section id="home" className="relative min-h-[100svh] w-full overflow-hidden flex items-center">
      <div className="absolute inset-0 z-0 will-change-transform" ref={imgRef} style={{ transform: "scale(1.08)" }}>
        <img
          src={MEDIA.hero}
          alt="A wooden boardwalk winding through calm sand dunes to the sea at golden hour"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </div>
      {/* Readability scrims — stronger on mobile, lighter on desktop-left */}
      <div className="absolute inset-0 z-10 bg-cream/45 lg:bg-transparent" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-cream via-cream/80 to-cream/30 lg:from-cream/90 lg:via-cream/55 lg:to-transparent" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-cream via-cream/40 to-cream/30 lg:via-transparent lg:to-cream/30" />

      <div ref={contentRef} className="relative z-20 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-24 pb-16 will-change-transform">
        <div className="max-w-3xl mx-auto lg:mx-0 text-center lg:text-left flex flex-col items-center lg:items-start">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.8 }}
            className="overline text-sage-dark mb-6 text-[0.62rem] sm:text-xs leading-loose"
          >
            <span className="block">Counselling &amp; Supervision</span>
            <span className="block">Swindon &amp; Online</span>
          </motion.p>

          <h1 className="font-serif text-ink leading-[1.08] pb-[0.08em] text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5.25rem] tracking-tight [text-wrap:balance]">
            <Headline />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-7 text-base sm:text-lg lg:text-xl text-ink max-w-xl leading-relaxed [text-wrap:balance]"
          >
            I'm a qualified counsellor offering warm, professional support in Swindon
            and online — a safe space to talk through anxiety, grief, stress, relationship
            difficulties and life's challenges.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-9 flex flex-col sm:flex-row gap-3.5 w-full sm:w-auto"
          >
            <button
              onClick={() => scrollToId("contact")}
              data-testid="hero-book-btn"
              className="rounded-full bg-sage-dark hover:bg-sage-deep text-cream px-8 py-4 text-sm font-medium tracking-wide shadow-float transition-all duration-300 active:scale-[0.98]"
            >
              Book your free 30-minute consultation
            </button>
            <button
              onClick={() => scrollToId("about")}
              data-testid="hero-learn-btn"
              className="rounded-full bg-white/80 hover:bg-white text-sage-dark border border-sage-light px-8 py-4 text-sm font-medium tracking-wide backdrop-blur-sm transition-all duration-300 active:scale-[0.98]"
            >
              Learn more
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row items-center gap-2.5 sm:gap-3"
          >
            <div className="flex gap-0.5" aria-label="Five star rating">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} className="fill-warm text-warm" />
              ))}
            </div>
            <p className="text-sm text-ink-soft">
              Trusted by clients across Swindon &amp; Wiltshire
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
